import React, { useEffect, useState } from "react";
import Api from "../../services/Api";
import CustomButton from "../../components/CustomButton";

interface AiSystem {
  id: number;
  name: string;
}

interface ClientPrompt {
  id: string;
  name: string;
  promptCoaching: string;
  promptNeutral: string;
}

const AiSystem: React.FC = () => {
  const api = Api.getInstance();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [aiSystem, setAiSystem] = useState(0);
  const [aiSystems, setAiSystems] = useState<Array<AiSystem>>([]);
  const [clientPrompt, setClientPrompt] = useState("default");
  const [clientPrompts, setClientPrompts] = useState<Array<ClientPrompt>>([]);

  useEffect(() => {
    api.get('client/ai_system').then(response => {
      setAiSystem(response.json.aiSystem);
      setAiSystems(response.json.aiSystems);
      setClientPrompt(response.json.clientPrompt);
      setClientPrompts(response.json.clientPrompts);
      setLoaded(true);
    })
  }, []);

  const changeSystem = () => {
    const data = {
      aiSystem: aiSystem,
      clientPrompt: clientPrompt,
    }
    api.post('client/ai_system', data).then(() => {
      alert("Updated your AI system");
    })
  }

  if (!loaded) {
    return <div className="mt-4">Loading...</div>;
  }

  const renderAiInfo = () => {
    if (aiSystem === 0) {
      return (
        <div className="mt-4">
          <p>This is the default system used by all clients so far.</p>
          <p className="mt-2">OpenAI hosted by Azure in Sweden is used for historical reasons, when this was the
            only way to guarantee that data stayed in EU.</p>
        </div>
      )
    } else if (aiSystem === 1) {
      return (
        <div className="mt-4">
          <p>You can send the AI questions directly to OpenAI.</p>
          <p className="mt-2 text-cherry-600">Data is not guaranteed to stay in EU.</p>
        </div>
      )
    } else if (aiSystem === 2) {
      return (
        <div className="mt-4">
          <p>Gemini is an upcoming star.</p>
          <p className="mt-2 text-cherry-600">Data is not guaranteed to stay in EU.</p>
        </div>
      )
    } else if (aiSystem === 3) {
      return (
        <div className="mt-4">
          <p>Mistral is a French system. They follow the EU laws by the book (e.g. AI Act).</p>
        </div>
      )
    } else if (aiSystem === 4) {
      return (
        <div className="mt-4">
          <p>Claude by Anthropic is a strong competitor to OpenAI.</p>
          <p className="mt-2 text-cherry-600">Data is not guaranteed to stay in EU.</p>
        </div>
      )
    }
  }

  const findSelectedPrompt = () => {
    for (const _clientPrompt of clientPrompts) {
      if (_clientPrompt.id === clientPrompt) {
        return _clientPrompt
      }
    }
  }

  const renderPromptInfo = () => {
    const selectedPrompt = findSelectedPrompt();
    if (selectedPrompt) {
      return (
        <div className="mt-4">
          <div className="font-medium">Selected Neutral style prompt:</div>
          <div className="mt-2 text-gray-400 p-2 bg-indigo-800 whitespace-pre-line">
            {selectedPrompt.promptNeutral}
          </div>

          <div className="mt-4 font-medium">Selected Coaching style prompt:</div>
          <div className="mt-2 text-gray-400 p-2 bg-indigo-800 whitespace-pre-line">
            {selectedPrompt.promptCoaching}
          </div>
        </div>
      )
    }
  }

  return (
    <div className="main-one-col small">

      <div className="z-h2 mt-6">Change your AI system</div>

      <div className="mt-6 text-gray-400">Testing new AI systems is complex.
        Ask tech before start testing.
      </div>

      <p className="mt-4 font-medium">AI system</p>
      <select value={aiSystem} className="z-select mt-1"
              onChange={(e) => setAiSystem(parseInt(e.target.value))}>
        {
          aiSystems.map((a, i) => (
            <option key={"ai_system_" + i} value={a.id}>{a.name}</option>
          ))
        }
      </select>

      {renderAiInfo()}

      <p className="mt-8 font-medium">Prompts</p>
      <select value={clientPrompt} className="z-select mt-1"
              onChange={(e) => setClientPrompt(e.target.value)}>
        {
          clientPrompts.map(prompt => (
            <option key={"client_prompt_" + prompt.id} value={prompt.id}>{prompt.name}</option>
          ))
        }
      </select>

      {renderPromptInfo()}

      <div className="mt-8">
        <CustomButton buttonType="blueBase" onClick={() => changeSystem()}>Change</CustomButton>
      </div>
    </div>
  );
};

export default AiSystem;
